import { isPlatformBrowser, LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { asyncScheduler, filter, map, of, Subject, switchMap, throttleTime } from 'rxjs';
import { MenuItem } from '../../../interfaces/website-menu';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { PICFLOW_STORYBLOK } from '../../../picflow-storyblok';
import { TranslateLinkPipe } from '../../../pipes/translate-link.pipe';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { BrowserService } from '../../../services/browser.service';
import { ThemeService } from '../../../services/theme.service';
import { WebsiteNavYieldService } from '../../../services/website-nav-yield.service';
import { StoryblokCdnPipe } from '../../../storyblok/pipes/storyblok-cdn.pipe';
import { storyblokLinkPipe } from '../../../storyblok/pipes/storyblok-link.pipe';
import { WEBSITE_HEADER_SLUG } from '../../../storyblok/website-chrome.service';
import { ButtonDirective } from '../../directives/button.directive';
import { ReplacePipe } from '../../pipes/replace.pipe';
import { SvgComponent } from '../svg/svg.component';
import { WebsiteDesktopMenuComponent } from '../website-desktop-menu/website-desktop-menu.component';

/** Rate-limit expensive under-nav `elementsFromPoint` sampling. */
const THEME_SAMPLE_MS = 80;

/** Keep the menu surface out of flow until the panel collapse finishes (matches SCSS 0.4s). */
const MENU_CLOSE_MS = 400;

@Component({
  selector: 'app-website-header',
  templateUrl: './website-header.component.html',
  styleUrls: ['./website-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    WebsiteDesktopMenuComponent,
    ButtonDirective,
    SvgComponent,
    NgTemplateOutlet,
    TranslatePipe,
    TranslateLinkPipe,
    storyblokLinkPipe,
    StoryblokCdnPipe,
    LowerCasePipe,
    ReplacePipe,
  ],
  host: {
    '[class.yielded]': 'yielded()',
    '[class.overlay]': 'overlay()',
    '[class.scrolled]': 'pill()',
    '[class.menu-open]': 'menuSurface()',
    '[attr.aria-hidden]': 'yielded() ? true : null',
  },
})
export class WebsiteHeaderComponent implements OnInit {
  private browserService = inject(BrowserService);
  private platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private storyblokService = inject(PICFLOW_STORYBLOK);
  private host = inject(ElementRef<HTMLElement>);
  private navYield = inject(WebsiteNavYieldService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private themeService = inject(ThemeService);
  private destroyRef = inject(DestroyRef);

  /** Rest/SSR chrome. See docs/WEBSITE-NAV-CHROME.md */
  readonly startTheme = input<'light' | 'dark'>(undefined);
  /** Pill fallback if no `.theme-*` under nav. */
  readonly pageTheme = input<'light' | 'dark'>(undefined);
  readonly hideNav = input(false);
  readonly overlay = input(false);
  readonly customCta = input<'contact' | undefined>(undefined);
  readonly yielded = this.navYield.yielded;

  scrolled = signal(false);
  private underNavIsDark = signal<boolean | null>(null);
  loggedIn = signal(false);
  /** Mobile menu expanded from the pill (skeleton). Drives the height animation. */
  menuOpen = signal(false);
  /**
   * True while the shell is out of flow (fixed) — from open until the close
   * animation ends. Rejoining the flow mid-collapse would animate the header's
   * in-flow height and move the page body.
   */
  menuSurface = signal(false);
  /**
   * Menu was opened at page top (no pill yet). Static → fixed can't transition,
   * so CSS animates the shell from the in-flow top-bar geometry to the pill
   * insets instead of snapping (logo/CTA would visibly jump 16px inward).
   */
  menuFromTop = signal(false);
  private menuCloseTimer: ReturnType<typeof setTimeout> | undefined;
  private removeOutsideListeners: (() => void) | undefined;

  /** rAF-coalesced scroll/resize pulses. */
  private readonly viewport$ = new Subject<void>();
  private scrollRaf = 0;

  /**
   * Rest → startTheme. Pill → under-nav `.theme-*`, else pageTheme. Open menu →
   * always light. Keyed to menuOpen (not menuSurface) so the fade back to the
   * page theme runs *during* the collapse instead of after it.
   */
  isDarkChrome = computed(() => {
    const menuOpen = this.menuOpen();
    const pill = this.pill();
    const resolvedTheme = this.themeService.resolvedTheme();
    const startTheme = this.startTheme() || resolvedTheme;
    const pageTheme = this.pageTheme();
    const underNavIsDark = this.underNavIsDark();

    if (menuOpen) return false;
    if (!pill) {
      if (startTheme) return startTheme === 'dark';
      return pageTheme == 'dark';
    }
    if (underNavIsDark !== null) return underNavIsDark;
    return pageTheme === 'dark';
  });

  /**
   * Pill chrome when scrolled — opening the menu at page top triggers it too.
   * Keyed to menuOpen so at page top the pill→top-bar morph overlaps the
   * collapse (menuSurface would defer it until the surface ends).
   */
  pill = computed(() => this.scrolled() || this.menuOpen());

  private header$ = this.route.data.pipe(
    switchMap(data => {
      if (data.header) return of(data.header);
      return this.storyblokService
        .getStory(WEBSITE_HEADER_SLUG, this.route.snapshot)
        .pipe(map(resp => (resp && resp.story) || (false as const)));
    }),
    map(data => data?.content as { menu: MenuItem[] })
  );
  readonly header = toSignal(this.header$);

  /**
   * Mobile accordion: null = default (first dropdown open), '' = all closed,
   * otherwise the open dropdown's text. Only one open at a time.
   */
  private accordionSelection = signal<string | null>(null);
  readonly openAccordion = computed(() => {
    const selected = this.accordionSelection();
    if (selected !== null) return selected;
    const menu = this.header()?.menu;
    return menu?.find(item => item.component === 'menu_dropdown')?.text ?? '';
  });

  constructor() {
    if (!this.isBrowser) return;

    this.destroyRef.onDestroy(() => {
      clearTimeout(this.menuCloseTimer);
      this.removeOutsideListeners?.();
      this.unlockPageScroll();
    });

    // Cheap: pill on/off every coalesced frame.
    this.viewport$.pipe(takeUntilDestroyed()).subscribe(() => this.updatePill());

    // Expensive: under-nav theme hit-test, capped while scrolling.
    this.viewport$
      .pipe(throttleTime(THEME_SAMPLE_MS, asyncScheduler, { leading: true, trailing: true }), takeUntilDestroyed())
      .subscribe(() => this.updateUnderNavTheme());

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.closeMobileMenu();
        this.underNavIsDark.set(null);
        this.navYield.setYielded(false);
        this.scheduleViewportUpdate();
      });
  }

  @HostListener('window:resize') winResize() {
    if (this.menuOpen() && window.innerWidth >= 900) this.closeMobileMenu();
    this.scheduleViewportUpdate();
  }
  @HostListener('window:scroll') docScroll() {
    this.scheduleViewportUpdate();
  }
  @HostListener('window:keydown', ['$event']) onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.menuOpen()) this.closeMobileMenu();
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;
    this.loggedIn.set(this.browserService.getCookies()?.['picflow-loggedin'] === 'true');
    this.scheduleViewportUpdate();
  }

  private scheduleViewportUpdate() {
    if (!this.isBrowser || this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = 0;
      this.viewport$.next();
    });
  }

  private updatePill() {
    const scrolled = (window.document.scrollingElement?.scrollTop ?? 0) > 24;
    this.scrolled.set(scrolled);
    if (!scrolled) this.underNavIsDark.set(null);
  }

  private updateUnderNavTheme() {
    if (this.menuOpen() || !this.scrolled()) {
      if (!this.scrolled()) this.underNavIsDark.set(null);
      return;
    }
    this.underNavIsDark.set(this.detectUnderNavIsDark());
  }

  /** Sample nearest `.theme-dark` / `.theme-light` under nav center. */
  private detectUnderNavIsDark(): boolean | null {
    const hostEl = this.host.nativeElement;
    const rect = hostEl.getBoundingClientRect();
    const x = Math.min(Math.max(window.innerWidth / 2, 0), window.innerWidth - 1);
    const y = Math.min(Math.max(rect.top + rect.height / 2, 0), window.innerHeight - 1);

    const stack = document.elementsFromPoint(x, y).filter((el): el is Element => {
      return el instanceof Element && el !== hostEl && !hostEl.contains(el);
    });

    for (const el of stack) {
      let node: Element | null = el;
      while (node && node !== document.documentElement && node !== document.body) {
        if (node.classList.contains('theme-dark')) return true;
        if (node.classList.contains('theme-light')) return false;
        node = node.parentElement;
      }
    }

    return null;
  }

  toggleAccordion(text: string) {
    this.accordionSelection.set(this.openAccordion() === text ? '' : text);
  }

  openMobileMenu() {
    if (!this.isBrowser || this.menuOpen() || this.hideNav()) return;
    clearTimeout(this.menuCloseTimer);
    this.accordionSelection.set(null);
    this.menuFromTop.set(!this.scrolled());
    this.menuSurface.set(true);
    this.menuOpen.set(true);
    this.lockPageScroll();
    this.addOutsideListeners();
  }

  closeMobileMenu() {
    if (!this.menuOpen()) return;
    this.menuOpen.set(false);
    this.removeOutsideListeners?.();
    // Resample the under-nav theme now so the chrome fade (forced-light → page
    // theme) starts with the collapse instead of after the surface ends.
    this.scheduleViewportUpdate();
    clearTimeout(this.menuCloseTimer);
    this.menuCloseTimer = setTimeout(() => {
      this.menuSurface.set(false);
      this.menuFromTop.set(false);
      this.unlockPageScroll();
    }, MENU_CLOSE_MS);
  }

  /**
   * Close on tap/click outside and swallow page pan gestures — replaces the
   * former full-viewport backdrop element: Safari 26+ samples fixed elements
   * near the viewport edges for its toolbar tint, and the corrupted tint
   * persisted after close.
   */
  private addOutsideListeners() {
    const hostEl = this.host.nativeElement as HTMLElement;
    const onPointerDown = (event: Event) => {
      if (!hostEl.contains(event.target as Node)) this.closeMobileMenu();
    };
    const onTouchMove = (event: TouchEvent) => {
      if (!hostEl.contains(event.target as Node)) event.preventDefault();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    this.removeOutsideListeners = () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('touchmove', onTouchMove);
      this.removeOutsideListeners = undefined;
    };
  }

  /**
   * Overflow-only lock: keeps scroll offset, no body reposition/jump.
   * Body only — never `documentElement`: hiding overflow on <html> makes Safari
   * collapse its dynamic chrome tint to solid, and it stays solid after unlock.
   * Touch scrolling on iOS is blocked by the backdrop's `touch-action: none`.
   */
  private lockPageScroll() {
    document.body.style.overflow = 'hidden';
  }

  private unlockPageScroll() {
    document.body.style.removeProperty('overflow');
  }

  openIntercom() {
    this.analytics.showIntercom();
  }
}
